// Backend integration point: replace localStorage with a secure session store / JWT context

export interface SubAccount {
  id: string;
  name: string;
  accountNumber: string;
  routingNumber: string;
  type: 'checking' | 'savings' | 'investment' | 'fixed';
  balance: number;
  createdAt: string;
}

export interface Transaction {
  id: string;
  date: string;
  description: string;
  counterparty: string;
  accountNumber: string;
  amount: number;
  type: 'debit' | 'credit';
  category: string;
  status: 'completed' | 'pending';
  reference: string;
  memo?: string;
}

export interface BankStore {
  isAuthenticated: boolean;
  availableBalance: number;
  lockedBalance: number;
  accounts: SubAccount[];
  transactions: Transaction[];
}

function generateAccountNumber(): string {
  return Array.from({ length: 10 }, () => Math.floor(Math.random() * 10)).join('');
}

function generateRoutingNumber(): string {
  return '0' + Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
}

function generateRef(): string {
  return 'BCB' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).substring(2, 6).toUpperCase();
}

// 2-year historical transactions (pre-loaded)
function buildHistoricalTransactions(): Transaction[] {
  const txns: Transaction[] = [];

  const debitTemplates = [
    { desc: 'Wire Transfer — Outgoing', counterparty: 'Meridian Capital LLC', category: 'Transfer' },
    { desc: 'ACH Payment — Utility', counterparty: 'ConEdison Services', category: 'Utilities' },
    { desc: 'ACH Payment — Property Tax', counterparty: 'NYC Dept of Finance', category: 'Taxes' },
    { desc: 'Wire Transfer — Investment', counterparty: 'Vanguard Private Wealth', category: 'Investment' },
    { desc: 'ACH Payment — Insurance', counterparty: 'Chubb Private Client', category: 'Insurance' },
    { desc: 'Wire Transfer — Outgoing', counterparty: 'Hartwell Holdings Inc', category: 'Transfer' },
    { desc: 'ACH Payment — Mortgage', counterparty: 'First Republic Lending', category: 'Mortgage' },
    { desc: 'Wire Transfer — Legal Retainer', counterparty: 'Sullivan & Cromwell LLP', category: 'Legal' },
    { desc: 'ACH Payment — Club Dues', counterparty: 'Metropolitan Club NYC', category: 'Lifestyle' },
    { desc: 'Wire Transfer — Outgoing', counterparty: 'Blackstone Advisory', category: 'Transfer' },
    { desc: 'ACH Payment — Charitable', counterparty: 'Moore Family Foundation', category: 'Charitable' },
    { desc: 'Wire Transfer — Travel', counterparty: 'NetJets Inc', category: 'Travel' },
  ];

  const creditTemplates = [
    { desc: 'Wire Transfer — Incoming', counterparty: 'Fernandes H. Moore Trust', category: 'Transfer' },
    { desc: 'Dividend Credit', counterparty: 'Blackrock Asset Mgmt', category: 'Investment' },
    { desc: 'Interest Credit', counterparty: 'BlueCrest Private Bank', category: 'Interest' },
    { desc: 'ACH Credit — Rental Income', counterparty: 'Park Ave Realty LLC', category: 'Income' },
    { desc: 'Wire Transfer — Incoming', counterparty: 'Sequoia Capital Distribution', category: 'Transfer' },
    { desc: 'Dividend Credit', counterparty: 'Fidelity Investments', category: 'Investment' },
    { desc: 'ACH Credit — Consulting Fee', counterparty: 'Moore Consulting Group', category: 'Income' },
  ];

  const now = new Date('2026-08-11');
  const twoYearsAgo = new Date('2024-08-11');

  let d = new Date(twoYearsAgo);
  let txnIdx = 1;

  while (d <= now) {
    // 3-6 transactions per week on average
    const txnsThisWeek = 2 + Math.floor(Math.random() * 4);
    for (let i = 0; i < txnsThisWeek; i++) {
      const isDebit = Math.random() > 0.38;
      const templates = isDebit ? debitTemplates : creditTemplates;
      const tpl = templates[Math.floor(Math.random() * templates.length)];

      const txnDate = new Date(d);
      txnDate.setDate(txnDate.getDate() + Math.floor(Math.random() * 7));
      if (txnDate > now) break;

      const baseAmount = isDebit
        ? (500 + Math.random() * 28000)
        : (800 + Math.random() * 35000);
      const amount = Math.round(baseAmount * 100) / 100;

      const mm = String(txnDate.getMonth() + 1).padStart(2, '0');
      const dd = String(txnDate.getDate()).padStart(2, '0');
      const yyyy = txnDate.getFullYear();

      txns.push({
        id: `txn-${String(txnIdx).padStart(4, '0')}`,
        date: `${mm}/${dd}/${yyyy}`,
        description: tpl.desc,
        counterparty: tpl.counterparty,
        accountNumber: '****' + String(1000 + Math.floor(Math.random() * 9000)),
        amount,
        type: isDebit ? 'debit' : 'credit',
        category: tpl.category,
        status: 'completed',
        reference: generateRef(),
        memo: '',
      });
      txnIdx++;
    }
    d.setDate(d.getDate() + 7);
  }

  return txns.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB.getTime() - dateA.getTime();
  });
}

const DEFAULT_ACCOUNTS: SubAccount[] = [
  {
    id: 'acct-001',
    name: 'BlueCrest Primary Checking',
    accountNumber: '4821739056',
    routingNumber: '021000021',
    type: 'checking',
    balance: 61528.00,
    createdAt: '01/15/2022',
  },
  {
    id: 'acct-002',
    name: 'BlueCrest Fixed Deposit',
    accountNumber: '7304851629',
    routingNumber: '021000021',
    type: 'fixed',
    balance: 317618.00,
    createdAt: '03/08/2022',
  },
  {
    id: 'acct-003',
    name: 'BlueCrest Savings Reserve',
    accountNumber: '9162043785',
    routingNumber: '021000021',
    type: 'savings',
    balance: 24800.00,
    createdAt: '06/20/2022',
  },
];

const STORAGE_KEY = 'bluecrest_store';

export function getStore(): BankStore {
  if (typeof window === 'undefined') {
    return {
      isAuthenticated: false,
      availableBalance: 61528.00,
      lockedBalance: 317618.00,
      accounts: DEFAULT_ACCOUNTS,
      transactions: buildHistoricalTransactions(),
    };
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as BankStore;
      return parsed;
    }
  } catch {
    // ignore
  }

  const initial: BankStore = {
    isAuthenticated: false,
    availableBalance: 61528.00,
    lockedBalance: 317618.00,
    accounts: DEFAULT_ACCOUNTS,
    transactions: buildHistoricalTransactions(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
  return initial;
}

export function saveStore(store: BankStore): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function addTransactionToStore(txn: Transaction): void {
  const store = getStore();
  store.transactions = [txn, ...store.transactions];
  store.availableBalance = Math.round((store.availableBalance - txn.amount) * 100) / 100;
  // Update primary checking account balance
  const primaryAcct = store.accounts.find((a) => a.id === 'acct-001');
  if (primaryAcct) {
    primaryAcct.balance = store.availableBalance;
  }
  saveStore(store);
}

export function addAccountToStore(account: SubAccount): void {
  const store = getStore();
  store.accounts.push(account);
  saveStore(store);
}

export function setAuthenticated(val: boolean): void {
  const store = getStore();
  store.isAuthenticated = val;
  saveStore(store);
}

export function generateNewAccountNumber(): string {
  return generateAccountNumber();
}

export function generateNewRoutingNumber(): string {
  return generateRoutingNumber();
}

export function generateTransactionRef(): string {
  return generateRef();
}

export function formatUSD(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}